import { useState, FormEvent } from 'react';
import { aboutInfo } from '@/mocks/site';

const FORM_URL = 'https://readdy.ai/api/form/d9tvt978l97n3eas3el0';

export default function AboutContact() {
  const { contact } = aboutInfo;
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        if (serverMsg && (typeof serverMsg === 'string') && serverMsg.includes('spam')) {
          setStatus('error');
          setErrorMsg('提交失败，请稍后重试。');
        } else {
          setStatus('error');
          setErrorMsg(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <section>
      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
        {contact.title}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-background-100 rounded-lg p-6 md:p-8">
          <h3 className="font-heading font-semibold text-foreground-900 text-base mb-5">联系方式</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                <i className="ri-map-pin-line text-primary-600"></i>
              </div>
              <div>
                <p className="text-foreground-500 text-xs mb-0.5">公司地址</p>
                <p className="text-foreground-800 text-sm">{contact.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                <i className="ri-mail-line text-primary-600"></i>
              </div>
              <div>
                <p className="text-foreground-500 text-xs mb-0.5">电子邮箱</p>
                <p className="text-foreground-800 text-sm">{contact.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                <i className="ri-phone-line text-primary-600"></i>
              </div>
              <div>
                <p className="text-foreground-500 text-xs mb-0.5">联系电话</p>
                <p className="text-foreground-800 text-sm">{contact.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                <i className="ri-wechat-line text-primary-600"></i>
              </div>
              <div>
                <p className="text-foreground-500 text-xs mb-0.5">微信公众号</p>
                <p className="text-foreground-800 text-sm">{contact.wechat}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-100 rounded-lg p-6 md:p-8">
          <h3 className="font-heading font-semibold text-foreground-900 text-base mb-5">发送消息</h3>
          <form onSubmit={handleSubmit} data-readdy-form="">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-foreground-700 text-xs mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-foreground-700 text-xs mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-foreground-700 text-xs mb-1.5">主题</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-foreground-700 text-xs mb-1.5">消息内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您想说的话（最多500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <button
                type="submit"
                className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 py-2.5 rounded-md font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                发送消息
              </button>

              {status === 'success' && (
                <p className="text-sm text-accent-600 text-center">消息发送成功！我们会尽快回复您。</p>
              )}
              {status === 'error' && (
                <p className="text-sm text-red-500 text-center">{errorMsg}</p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}